import { useMemo } from "react";
import { bool, number, object } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { NumericFormat } from "react-number-format";
import pickBy from "lodash/pickBy";
import { PriceFormats, Rounding, roundFloat } from "@repo/utils";
import { useEstimateStructure } from "@tools";
import { Divider, Grid } from "@material-ui/core";

export const BufferTooltipContent = observer(({
  isPrice,
  cleanValue,
  bufferedValues,
  splitBufferTypes,
}) => {
  const { t } = useTranslation();
  const structure = useEstimateStructure();
  
  const { modifier, timeModifier, currencyObj, priceFormat } = structure.settings;
  
  const formatValues = (value, valueIsModified= false) => {
    if(isPrice)
      return roundFloat(Math.abs(value), Rounding.DECIMALS, true);
    if(valueIsModified)
      return Math.abs(value);
    
    return Math.abs(Number((value / modifier).toFixed(2)))
  }
  
  const mainBuffers = useMemo(() => (
    splitBufferTypes
      ? Object.keys(bufferedValues).filter((bufferId) => !bufferedValues[bufferId].itemBuffer)
      : Object.keys(bufferedValues)
  ), [bufferedValues]);
  
  const prefix = useMemo(() => (
    isPrice ? currencyObj.symbolStart : undefined
  ), [ isPrice, currencyObj ]);
  
  const suffix = useMemo(() => (
    isPrice
      ? (
        currencyObj.symbolEnd
        ? " " + currencyObj.symbolEnd
        : undefined
      )
      : " " + t(`times.${timeModifier === 1 ? "hour" : "day"}`)
  ), [ isPrice, currencyObj, timeModifier ]);
  
  const itemBuffers = useMemo(() => (
    splitBufferTypes
      ? Object.values(pickBy(bufferedValues, (v) => v.itemBuffer))
        .reduce((total, { value }) => total + value, 0)
      : undefined
  ), [bufferedValues]);
  
  return <Grid item container direction="column" className="font-bold">
    <span>
      { t("common.base_value") }
      {": "}
      <strong>
        <NumericFormat
          value={formatValues(cleanValue, true)}
          displayType="text"
          thousandSeparator={priceFormat === PriceFormats.AMERICAN ? "," : " "}
          decimalSeparator="."
          valueIsNumericString={isPrice}
          decimalScale={2}
          prefix={prefix}
          suffix={suffix}
        />
      </strong>
    </span>
    {
      Boolean(itemBuffers || mainBuffers.length) &&
      <Divider className="bg-secondary my-1 transparent-1" />
    }
    {
      mainBuffers
        .map((bufferId) => {
          const name = bufferedValues[bufferId].name;
          const value = bufferedValues[bufferId].value;
          
          return (
            <span key={bufferId}>
              {name}{": "}
              <strong>
                { value >= 0 ? "+" : "-" }
                {/*{ formatValues(value) }*/}
                <NumericFormat
                  value={formatValues(value)}
                  displayType="text"
                  thousandSeparator={priceFormat === PriceFormats.AMERICAN ? "," : " "}
                  decimalSeparator="."
                  valueIsNumericString={isPrice}
                  decimalScale={2}
                  prefix={prefix}
                  suffix={suffix}
                />
              </strong>
            </span>
          )
        })
    }
    {
      Boolean(itemBuffers && mainBuffers.length) &&
      <Divider className="bg-secondary my-1 transparent-1" />
    }
    {
      Boolean(itemBuffers) &&
      <span>
        {t("views.editor.dialogs.buffers.subtitle.item")}{": "}
        <strong>
          { itemBuffers >= 0 ? "+" : "-" }
          <NumericFormat
            value={formatValues(itemBuffers)}
            displayType="text"
            thousandSeparator={priceFormat === PriceFormats.AMERICAN ? "," : " "}
            decimalSeparator="."
            valueIsNumericString={isPrice}
            decimalScale={2}
            prefix={prefix}
            suffix={suffix}
          />
        </strong>
      </span>
    }
  </Grid>
})

BufferTooltipContent.propTypes = {
  cleanValue: number.isRequired,
  bufferedValues: object.isRequired,
  isPrice: bool,
  splitBufferTypes: bool,
}